import { useId } from 'react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { Label } from './Label';

/** Prop a11y mà `Field` truyền xuống control. Rải hết lên ô nhập. */
export interface FieldControlProps {
  id: string;
  'aria-invalid': boolean;
  'aria-describedby'?: string;
}

export interface FieldProps {
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  /**
   * `id` của control, khi nhãn nằm ngoài `Field` (bố cục hai cột: nhãn bên
   * trái, ô nhập bên phải). Bỏ trống thì tự sinh.
   */
  id?: string;
  children: (props: FieldControlProps) => ReactNode;
}

/**
 * Hợp đồng của một trường biểu mẫu (§9.3.4): nhãn + control + gợi ý + lỗi, kèm
 * đủ dây a11y.
 *
 * Dùng render-prop để control nhận đúng `id`, `aria-invalid` và
 * `aria-describedby`. Lỗi **đè** gợi ý và làm ẩn gợi ý: hiện cả hai thì người
 * dùng phải tự đoán dòng nào đang nói về sai sót của mình.
 */
export function Field({ label, required, hint, error, className, id: idNgoai, children }: FieldProps) {
  const idTuSinh = useId();
  const id = idNgoai ?? idTuSinh;
  const errorId = `${id}-err`;
  const hintId = `${id}-hint`;

  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}

      {children({
        id,
        'aria-invalid': Boolean(error),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      })}

      {error ? (
        <p id={errorId} role="alert" className="text-xs text-danger-600">
          {error}
        </p>
      ) : (
        hint && (
          <p id={hintId} className="text-xs text-content-muted">
            {hint}
          </p>
        )
      )}
    </div>
  );
}
